import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Loader2, X, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const emptyForm = {
  name: '',
  delay_hours: 0,
  deposito: 0,
  deposito_gasolinas: 0,
  deposito_diesel: 0,
  diferencial_flete: 0,
  auto_full: false,
  diferencial_auto_full: 0,
  preset_notes: [],
};

const AdminDepartments = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/departments').then(r => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openEdit = (d) => {
    setEditing(d.id);
    setForm({
      name: d.name,
      delay_hours: d.delay_hours ?? 0,
      deposito: d.deposito ?? 0,
      deposito_gasolinas: d.deposito_gasolinas ?? d.deposito ?? 0,
      deposito_diesel: d.deposito_diesel ?? d.deposito ?? 0,
      diferencial_flete: d.diferencial_flete ?? 0,
      auto_full: !!d.auto_full,
      diferencial_auto_full: d.diferencial_auto_full ?? 0,
      preset_notes: d.preset_notes || [],
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/departments/${editing}`, { ...form, deposito: form.deposito_gasolinas });
        toast.success('Departamento actualizado');
      } else {
        await api.post('/departments', { ...form, deposito: form.deposito_gasolinas });
        toast.success('Departamento creado');
      }
      setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const addNote = () => setForm(f => ({
    ...f,
    preset_notes: [...f.preset_notes, { id: '', title: '', text: '', __k: `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }],
  }));
  const updateNote = (i, field, value) => setForm(f => ({
    ...f,
    preset_notes: f.preset_notes.map((n, idx) => idx === i ? { ...n, [field]: value } : n),
  }));
  const removeNote = (i) => setForm(f => ({ ...f, preset_notes: f.preset_notes.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-6" data-testid="admin-departments-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-[36px] leading-tight tracking-tight">Departamentos</h2>
          <p className="text-[13px] text-[#5a5a5a] mt-1">
            Reglas por departamento · {items.length} departamentos · delay, depósito, flete y notas para push de precios
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }} className="h-10 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12.5px]" data-testid="btn-new-dept">
          <Plus size={14} className="mr-1.5"/> Nuevo departamento
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-lg bg-[#f8f6f1] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? 'Editar departamento' : 'Nuevo departamento'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Nombre</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white" data-testid="input-dept-name"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Delay subida (h)</Label>
                <Input type="number" step="0.5" value={form.delay_hours} onChange={e => setForm({ ...form, delay_hours: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" data-testid="input-dept-delay"/>
                <p className="text-[10.5px] text-[#8a8579] mt-1">Horas que se posterga el cambio.</p>
              </div>
              <div>
                <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Depósito gasolinas</Label>
                <Input type="number" step="0.01" value={form.deposito_gasolinas} onChange={e => setForm({ ...form, deposito_gasolinas: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" data-testid="input-dept-deposito-gas"/>
                <p className="text-[10.5px] text-[#8a8579] mt-1">V-Power, Súper y Regular.</p>
              </div>
              <div>
                <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Depósito diésel</Label>
                <Input type="number" step="0.01" value={form.deposito_diesel} onChange={e => setForm({ ...form, deposito_diesel: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" data-testid="input-dept-deposito-diesel"/>
                <p className="text-[10.5px] text-[#8a8579] mt-1">Diésel y ULSD.</p>
              </div>
              <div>
                <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Diferencial flete</Label>
                <Input type="number" step="0.01" value={form.diferencial_flete} onChange={e => setForm({ ...form, diferencial_flete: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" data-testid="input-dept-flete"/>
              </div>
              <div className="flex items-end gap-2 pb-1">
                <div className="flex items-center gap-2">
                  <Switch checked={form.auto_full} onCheckedChange={v => setForm({ ...form, auto_full: v })} data-testid="switch-dept-autofull"/>
                  <Label className="text-[12px]">Auto / Full</Label>
                </div>
              </div>
            </div>

            {form.auto_full && (
              <div>
                <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Diferencial Auto / Full</Label>
                <Input type="number" step="0.01" value={form.diferencial_auto_full} onChange={e => setForm({ ...form, diferencial_auto_full: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" data-testid="input-dept-autofull-diff"/>
                <p className="text-[10.5px] text-[#8a8579] mt-1">Se suma al precio de servicio completo (full).</p>
              </div>
            )}

            <div className="bg-white border border-[#e8e5de] rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Notas pregrabadas</Label>
                <Button onClick={addNote} variant="outline" className="h-7 text-[11px] px-2" data-testid="btn-add-note"><Plus size={11} className="mr-1"/> Añadir</Button>
              </div>
              {form.preset_notes.length === 0 && <p className="text-[11.5px] text-[#8a8579]">Sin notas. Estas aparecen al enviar un push de precios.</p>}
              <div className="space-y-2">
                {form.preset_notes.map((n, i) => (
                  <div key={n.id || n.__k || `note-${i}`} className="border border-[#e8e5de] rounded-md p-2 space-y-1.5">
                    <div className="flex gap-2">
                      <Input value={n.title} onChange={e => updateNote(i, 'title', e.target.value)} placeholder="Título" className="h-8 text-[12px] bg-[#fafaf6]"/>
                      <button onClick={() => removeNote(i)} className="text-[#7c1d2e] p-1"><Trash2 size={13}/></button>
                    </div>
                    <Textarea value={n.text} onChange={e => updateNote(i, 'text', e.target.value)} placeholder="Texto de la nota…" className="text-[12px] bg-[#fafaf6] min-h-[52px]"/>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black" data-testid="btn-save-dept"><Save size={13} className="mr-1.5"/> Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        {loading ? <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div> : (
          <table className="w-full text-[13px]">
            <thead className="bg-[#f8f6f1]">
              <tr className="text-left text-[11px] tracking-wider uppercase text-[#8a8579] border-b border-[#e8e5de]">
                <th className="py-3 px-4 font-normal">Departamento</th>
                <th className="py-3 px-2 font-normal">Delay</th>
                <th className="py-3 px-2 font-normal">Dep. gasolinas</th>
                <th className="py-3 px-2 font-normal">Dep. diésel</th>
                <th className="py-3 px-2 font-normal">Dif. flete</th>
                <th className="py-3 px-2 font-normal">Auto/Full</th>
                <th className="py-3 px-2 font-normal">Notas</th>
                <th className="py-3 px-4 font-normal text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e8e5de]">
              {items.map(d => (
                <tr key={d.id} className="hover:bg-[#fafaf6]" data-testid={`row-dept-${d.name}`}>
                  <td className="py-3 px-4 font-medium">{d.name}</td>
                  <td className="py-3 px-2">{d.delay_hours} h</td>
                  <td className="py-3 px-2">{Number(d.deposito_gasolinas ?? d.deposito ?? 0).toFixed(2)}</td>
                  <td className="py-3 px-2">{Number(d.deposito_diesel ?? d.deposito ?? 0).toFixed(2)}</td>
                  <td className="py-3 px-2">{Number(d.diferencial_flete).toFixed(2)}</td>
                  <td className="py-3 px-2">
                    {d.auto_full ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#dcfce7] text-[#166534]">+{Number(d.diferencial_auto_full).toFixed(2)}</span> : <span className="text-[11px] text-[#8a8579]">—</span>}
                  </td>
                  <td className="py-3 px-2 text-[12px]">{(d.preset_notes || []).length}</td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => openEdit(d)} className="text-[12px] text-[#1c1c1c] hover:underline" data-testid={`btn-edit-dept-${d.name}`}>Editar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminDepartments;
